(function () {
  "use strict";

  const SELECTORS = {
    uploadRoot: "[data-casecase-ai-upload]",
    directUploadRoot: "quick-order-upload[data-product-form-id]",
    trigger: "[data-ai-upload-trigger]",
    input: "[data-ai-upload-input]",
    remove: "[data-ai-upload-remove]",
    status: "[data-ai-upload-status]",
    generatedInput: "[data-ai-generated-image-input]",
    displayInput: "[data-ai-generated-display-input]",
    jobIdInput: "[data-ai-generated-job-id-input]",
    historyTrigger: "[data-ai-upload-history-trigger]",
    productFormComponent: "product-form-component",
    acceleratedCheckout: ".accelerated-checkout-block",
    productFormButtons: "[ref='productFormButtons'], .product-form-buttons",
    addToCartButton: "[ref='addToCartButton'], button[name='add']",
  };

  const READY_CLASS = "casecase-ai-upload-ready";
  const GATED_CLASS = "casecase-ai-upload-gated";

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value);
    }
    return String(value).replace(/["\\]/g, "\\$&");
  }

  function getProductForm(uploadRoot) {
    const formId = uploadRoot?.dataset.productFormId;
    if (!formId) return null;
    return document.getElementById(formId);
  }

  function getProductFormComponent(uploadRoot) {
    const form = getProductForm(uploadRoot);
    return form?.closest(SELECTORS.productFormComponent) || null;
  }

  function getUploadRootFromForm(form) {
    if (!form?.id) return null;
    return document.querySelector(`${SELECTORS.uploadRoot}[data-product-form-id="${escapeSelector(form.id)}"]`);
  }

  function requiresImageUpload(form) {
    const component = form?.closest(SELECTORS.productFormComponent);
    const value = component?.dataset.casecaseRequiresImageUpload;
    return value === undefined ? null : value !== "false";
  }

  function getGeneratedInput(uploadRoot) {
    return uploadRoot?.querySelector(SELECTORS.generatedInput) || null;
  }

  function hasGeneratedImage(uploadRoot) {
    const input = getGeneratedInput(uploadRoot);
    return Boolean(input && typeof input.value === "string" && input.value.trim() !== "");
  }

  function setStatus(uploadRoot, message, state) {
    const status = uploadRoot?.querySelector(SELECTORS.status);
    if (!status) return;
    status.textContent = "";
    status.className = `ai-upload-status${state ? ` ${state}` : ""}`;
    status.hidden = true;
  }

  function setUploadReady(uploadRoot, ready) {
    if (!uploadRoot) return;
    const component = getProductFormComponent(uploadRoot);
    const form = getProductForm(uploadRoot);
    const acceleratedCheckout = component?.querySelector(SELECTORS.acceleratedCheckout);
    const addToCartButton = component?.querySelector(SELECTORS.addToCartButton);
    const canAddToCart = addToCartButton?.dataset.casecaseCanAddToCart !== "false";
    const canUseAcceleratedCheckout = acceleratedCheckout?.dataset.casecaseCanAddToCart !== "false";

    uploadRoot.classList.toggle(READY_CLASS, ready);
    uploadRoot.classList.toggle(GATED_CLASS, !ready);

    if (form) {
      const formReady = ready ? "true" : "false";
      if (form.dataset.casecaseGeneratedImageReady !== formReady) {
        form.dataset.casecaseGeneratedImageReady = formReady;
      }
    }

    if (component) {
      const componentReady = ready ? "true" : "false";
      if (component.dataset.casecaseGeneratedImageReady !== componentReady) {
        component.dataset.casecaseGeneratedImageReady = componentReady;
      }
    }

    if (acceleratedCheckout) {
      const shouldHide = !ready || !canUseAcceleratedCheckout;
      const ariaHidden = ready && canUseAcceleratedCheckout ? "false" : "true";
      if (acceleratedCheckout.hidden !== shouldHide) {
        acceleratedCheckout.hidden = shouldHide;
      }
      if (acceleratedCheckout.getAttribute("aria-hidden") !== ariaHidden) {
        acceleratedCheckout.setAttribute("aria-hidden", ariaHidden);
      }
    }

    if (addToCartButton) {
      const shouldDisable = !ready || !canAddToCart;
      const ariaDisabled = ready && canAddToCart ? "false" : "true";
      if (addToCartButton.disabled !== shouldDisable) {
        addToCartButton.disabled = shouldDisable;
      }
      if (addToCartButton.getAttribute("aria-disabled") !== ariaDisabled) {
        addToCartButton.setAttribute("aria-disabled", ariaDisabled);
      }
    }
  }

  function showValidationError(uploadRoot) {
    const trigger = uploadRoot?.querySelector(SELECTORS.trigger);
    if (trigger) {
      trigger.classList.add("validation-error");
      trigger.focus({ preventScroll: true });
      trigger.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setStatus(uploadRoot, "Please generate and select an image before checkout.", "error");
  }

  function validateForm(form) {
    const imageUploadRequired = requiresImageUpload(form);
    if (imageUploadRequired === null || imageUploadRequired === false) return true;
    const uploadRoot = getUploadRootFromForm(form);
    if (!uploadRoot) return true;
    if (hasGeneratedImage(uploadRoot)) return true;
    showValidationError(uploadRoot);
    return false;
  }

  function getDirectUploadRootFromForm(form) {
    if (!form?.id) return null;
    return document.querySelector(
      `${SELECTORS.directUploadRoot}[data-product-form-id="${escapeSelector(form.id)}"]`
    );
  }

  function releaseFormsWithoutUploadRoot() {
    document.querySelectorAll(SELECTORS.productFormComponent).forEach((component) => {
      if (component.dataset.casecaseRequiresImageUpload !== "true") return;
      const form = component.querySelector('form[data-type="add-to-cart-form"]');
      if (!form || getUploadRootFromForm(form) || getDirectUploadRootFromForm(form)) return;

      component.dataset.casecaseRequiresImageUpload = "false";
      const addToCartButton = component.querySelector(SELECTORS.addToCartButton);
      const acceleratedCheckout = component.querySelector(SELECTORS.acceleratedCheckout);
      const canAddToCart = addToCartButton?.dataset.casecaseCanAddToCart !== "false";
      const canUseAcceleratedCheckout = acceleratedCheckout?.dataset.casecaseCanAddToCart !== "false";

      if (addToCartButton) {
        addToCartButton.disabled = !canAddToCart;
        addToCartButton.setAttribute("aria-disabled", canAddToCart ? "false" : "true");
        addToCartButton.removeAttribute("data-casecase-upload-gated");
      }
      if (acceleratedCheckout) {
        acceleratedCheckout.hidden = !canUseAcceleratedCheckout;
        acceleratedCheckout.setAttribute("aria-hidden", canUseAcceleratedCheckout ? "false" : "true");
        acceleratedCheckout.removeAttribute("data-casecase-upload-gated");
      }
    });
  }

  function openUpsellIfAvailable(form) {
    // Upsell popup disabled - return false to proceed with normal add to cart
    return false;
  }

  function openUpsellForUploadRoot(uploadRoot) {
    // Upsell popup disabled - return false
    return false;
  }

  function scheduleUpsellForUploadRoot(uploadRoot) {
    window.setTimeout(() => {
      const generatedImage = getGeneratedInput(uploadRoot)?.value?.trim() || "";
      if (!generatedImage) {
        return;
      }
      if (uploadRoot.dataset.casecaseUpsellOpenedFor === generatedImage) {
        return;
      }
      if (openUpsellForUploadRoot(uploadRoot)) {
        uploadRoot.dataset.casecaseUpsellOpenedFor = generatedImage;
      }
    }, 360);
  }

  function resetUpload(uploadRoot) {
    const trigger = uploadRoot.querySelector(SELECTORS.trigger);
    const preview = trigger?.querySelector(".ai-upload-preview");
    const fileInput = uploadRoot.querySelector(SELECTORS.input);
    const generatedInput = uploadRoot.querySelector(SELECTORS.generatedInput);
    const displayInput = uploadRoot.querySelector(SELECTORS.displayInput);
    const jobIdInput = uploadRoot.querySelector(SELECTORS.jobIdInput);

    if (generatedInput) generatedInput.value = "";
    if (displayInput) displayInput.value = "";
    if (jobIdInput) jobIdInput.value = "";
    if (fileInput) fileInput.value = "";
    if (preview) preview.removeAttribute("src");
    if (trigger) trigger.classList.remove("has-image", "validation-error");
    delete uploadRoot.dataset.casecaseUpsellOpenedFor;
    setStatus(uploadRoot, "", "");
    setUploadReady(uploadRoot, false);
  }

  function syncSelectedOptions(uploadRoot) {
    const component = getProductFormComponent(uploadRoot);
    const variantPicker =
      component?.parentElement?.querySelector("variant-picker") ||
      document.querySelector(`variant-picker[data-section-id="${escapeSelector(uploadRoot.dataset.sectionId || "")}"]`);
    const checkedInputs = variantPicker?.querySelectorAll('input[type="radio"]:checked') || [];
    const selectedInputs = variantPicker?.querySelectorAll("select.variant-option__select") || [];
    const selectedOptions = {
      variantId: getProductForm(uploadRoot)?.querySelector('input[name="id"]')?.value || "",
      productHandle: window.location.pathname.split("/products/")[1]?.split("?")[0] || "",
    };

    checkedInputs.forEach((input, index) => {
      const value = input.value;
      if (index === 0) selectedOptions.size = value;
      if (index === 1) selectedOptions.style = value;
      if (index === 2) selectedOptions.option3 = value;
    });

    selectedInputs.forEach((select, index) => {
      const value = select.value;
      const label = variantPicker?.querySelector(`label[for="${escapeSelector(select.id)}"]`)?.textContent?.trim() || "";
      const normalizedLabel = label.toLowerCase();

      if (normalizedLabel.includes("figure") || normalizedLabel.includes("style")) {
        selectedOptions.style = value;
      } else if (normalizedLabel.includes("model")) {
        selectedOptions.model = value;
      } else if (index === 0) {
        selectedOptions.style = value;
      } else if (index === 1) {
        selectedOptions.model = value;
      } else {
        selectedOptions[`option${index + 1}`] = value;
      }
    });

    window.selectedProductOptions = selectedOptions;
  }

  function initUploader(uploadRoot) {
    if (uploadRoot.dataset.casecaseAiInitialized === "true") return;
    if (typeof window.ImageUploader !== "function") return;

    const trigger = uploadRoot.querySelector(SELECTORS.trigger);
    const fileInput = uploadRoot.querySelector(SELECTORS.input);
    const hiddenInput = uploadRoot.querySelector(SELECTORS.generatedInput);
    const removeButton = uploadRoot.querySelector(SELECTORS.remove);

    if (!trigger || !fileInput || !hiddenInput) return;

    uploadRoot.dataset.casecaseAiInitialized = "true";
    setUploadReady(uploadRoot, hasGeneratedImage(uploadRoot));

    const uploader = new window.ImageUploader({
      onUploadStart: () => {
        syncSelectedOptions(uploadRoot);
        setStatus(uploadRoot, "Generating your design...", "warning");
        setUploadReady(uploadRoot, false);
      },
      onImageSelect: (imageUrl) => {
        const preview = trigger.querySelector(".ai-upload-preview");
        if (preview) preview.src = imageUrl;
        trigger.classList.add("has-image");
        trigger.classList.remove("validation-error");
        setStatus(uploadRoot, "Design selected.", "success");
        setUploadReady(uploadRoot, true);
        scheduleUpsellForUploadRoot(uploadRoot);
      },
      onUploadError: (error) => {
        setStatus(uploadRoot, error?.message || "Upload failed. Please try again.", "error");
        setUploadReady(uploadRoot, false);
      },
    });

    uploader.getCurrentContainer = function () {
      return uploadRoot.closest(".product-information, .product-details, .shopify-section") || document.body;
    };

    uploader.fetchSelectedOptions = async function () {
      syncSelectedOptions(uploadRoot);
    };

    const originalSaveImageUrl = uploader.saveImageUrl.bind(uploader);
    uploader.saveImageUrl = function (url, displayUrl, jobId) {
      originalSaveImageUrl(url, displayUrl, jobId);
      const displayInput = uploadRoot.querySelector(SELECTORS.displayInput);
      const jobIdInput = uploadRoot.querySelector(SELECTORS.jobIdInput);
      if (hiddenInput) hiddenInput.value = url || "";
      if (displayInput) displayInput.value = displayUrl || url || "";
      if (jobIdInput) jobIdInput.value = jobId || "";
      hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
      setUploadReady(uploadRoot, Boolean(url));
      if (url) {
        scheduleUpsellForUploadRoot(uploadRoot);
      }
    };

    uploader.init(trigger, fileInput, hiddenInput);
    uploadRoot._casecaseUploader = uploader;

    removeButton?.addEventListener("click", (event) => {
      event.preventDefault();
      resetUpload(uploadRoot);
    });

    uploadRoot.addEventListener("change", (event) => {
      if (event.target === hiddenInput) {
        setUploadReady(uploadRoot, hasGeneratedImage(uploadRoot));
        if (hasGeneratedImage(uploadRoot)) {
          scheduleUpsellForUploadRoot(uploadRoot);
        }
      }
    });
  }

  function setupHistoryDelegation() {
    if (window._casecaseUploadHistoryDelegationAttached) return;
    window._casecaseUploadHistoryDelegationAttached = true;

    document.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest(SELECTORS.historyTrigger);
        if (!button) return;
        const uploadRoot = button.closest(SELECTORS.uploadRoot);
        if (!uploadRoot || !window.globalUploadModal) return;

        event.preventDefault();
        event.stopPropagation();

        syncSelectedOptions(uploadRoot);
        const modal = window.globalUploadModal;
        const productFormId = uploadRoot.dataset.productFormId || "";
        modal.openedDirectlyToHistory = true;
        modal.confirmCallback = function () {
          const currentUploadRoot =
            productFormId
              ? document.querySelector(`${SELECTORS.uploadRoot}[data-product-form-id="${escapeSelector(productFormId)}"]`)
              : uploadRoot;
          const selectedUrl = modal.getSelectedImageUrl();
          const selectedDisplayUrl = modal.getSelectedDisplayUrl();
          const selectedJobId = modal.getSelectedJobId();
          const trigger = currentUploadRoot?.querySelector(SELECTORS.trigger);
          const preview = trigger?.querySelector(".ai-upload-preview");
          const generatedInput = currentUploadRoot?.querySelector(SELECTORS.generatedInput);
          const displayInput = currentUploadRoot?.querySelector(SELECTORS.displayInput);
          const jobIdInput = currentUploadRoot?.querySelector(SELECTORS.jobIdInput);

          if (!selectedUrl || !generatedInput) return;

          generatedInput.value = selectedUrl;
          if (displayInput) displayInput.value = selectedDisplayUrl || selectedUrl;
          if (jobIdInput) jobIdInput.value = selectedJobId || "";
          if (preview) preview.src = selectedDisplayUrl || selectedUrl;
          if (trigger) trigger.classList.add("has-image");

          generatedInput.dispatchEvent(new Event("change", { bubbles: true }));
          setStatus(currentUploadRoot, "Design selected from history.", "success");
          setUploadReady(currentUploadRoot, true);
          modal.openedDirectlyToHistory = false;
          scheduleUpsellForUploadRoot(currentUploadRoot);
        };

        modal.open();
        modal.showHistory();
      },
      true
    );
  }

  function setupGlobalConfirmFallback() {
    if (window._casecaseUploadConfirmFallbackAttached) return;
    window._casecaseUploadConfirmFallbackAttached = true;

    document.addEventListener(
      "click",
      (event) => {
        const confirmButton = event.target.closest(".global-upload-modal-confirm, #btnConfirmHistory");
        if (!confirmButton) return;

        window.setTimeout(() => {
          const readyRoots = Array.from(document.querySelectorAll(SELECTORS.uploadRoot)).filter(hasGeneratedImage);
          const uploadRoot = readyRoots.find((root) => root.dataset.casecaseUpsellOpenedFor !== getGeneratedInput(root)?.value?.trim()) || readyRoots[0];
          if (uploadRoot) {
            scheduleUpsellForUploadRoot(uploadRoot);
          }
        }, 420);
      },
      true
    );
  }

  function setupFormValidation() {
    if (window._casecaseUploadFormValidationAttached) return;
    window._casecaseUploadFormValidationAttached = true;

    document.addEventListener(
      "submit",
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || form.dataset.type !== "add-to-cart-form") return;
        if (!validateForm(form)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }
        if (!openUpsellIfAvailable(form)) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      },
      true
    );

    document.addEventListener(
      "click",
      (event) => {
        const paymentButton = event.target.closest(".shopify-payment-button, .shopify-payment-button *");
        if (!paymentButton) return;
        const component = paymentButton.closest(SELECTORS.productFormComponent);
        const form = component?.querySelector('form[data-type="add-to-cart-form"]');
        if (!form || validateForm(form)) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      },
      true
    );
  }

  function initAll() {
    document.querySelectorAll(SELECTORS.uploadRoot).forEach((uploadRoot) => {
      if (uploadRoot.dataset.casecaseAiInitialized === "true") {
        setUploadReady(uploadRoot, hasGeneratedImage(uploadRoot));
      } else {
        initUploader(uploadRoot);
      }
    });
    releaseFormsWithoutUploadRoot();
    setupHistoryDelegation();
    setupGlobalConfirmFallback();
    setupFormValidation();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  new MutationObserver(initAll).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
